import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../ui/alert-dialog";
import { getAdminKey, setAdminKey, verifyAdmin, fetchLeads, updateLeadStatus, deleteLead, errMsg } from "../../lib/api";

const statusStyle = {
  new: "bg-yellow-400 text-black",
  contacted: "bg-sky-500/20 text-sky-300",
  closed: "bg-zinc-700 text-white/70",
};

function Login({ onAuthed }) {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await verifyAdmin(key);
      setAdminKey(key);
      onAuthed();
    } catch (err) {
      toast.error(errMsg(err, "Invalid passcode"));
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-black">
      <form onSubmit={submit} className="w-full max-w-md rounded-3xl bg-zinc-900 border border-white/10 p-10" data-testid="admin-login">
        <div className="w-12 h-12 rounded-2xl bg-yellow-400 flex items-center justify-center"><Icons.Lock className="w-5 h-5 text-black" /></div>
        <h1 className="font-display text-3xl font-bold text-white mt-6">Lead Inbox</h1>
        <p className="text-white/55 mt-2">Enter the admin passcode to view messages and quote requests.</p>
        <input
          type="password"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="Admin passcode"
          data-testid="admin-passcode"
          className="mt-8 w-full bg-black border border-white/15 rounded-xl px-4 py-3 text-white outline-none focus:border-yellow-400 transition-colors"
        />
        <button disabled={busy || !key} data-testid="admin-login-btn" className="mt-5 w-full bg-yellow-400 text-black font-bold py-3.5 rounded-xl hover:bg-yellow-300 disabled:opacity-50 transition-colors">
          {busy ? "Checking..." : "Unlock"}
        </button>
        <Link to="/" className="block text-center text-white/45 hover:text-yellow-400 text-sm mt-6 transition-colors">Back to website</Link>
      </form>
    </div>
  );
}

function LeadRow({ lead, onStatus, onDelete }) {
  const [open, setOpen] = useState(false);
  const d = new Date(lead.createdAt);
  return (
    <div className="rounded-2xl bg-zinc-900 border border-white/10 hover:border-white/20 transition-colors" data-testid={`lead-${lead.id}`}>
      <div className="p-5 grid md:grid-cols-[auto_1.4fr_1fr_auto_auto] gap-4 items-center">
        <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${lead.type === "quote" ? "bg-yellow-400/15" : "bg-white/5"}`}>
          {lead.type === "quote" ? <Icons.FileText className="w-4 h-4 text-yellow-400" /> : <Icons.Mail className="w-4 h-4 text-white/70" />}
        </span>
        <button onClick={() => setOpen(!open)} className="text-left min-w-0" data-testid="lead-toggle">
          <div className="text-white font-semibold truncate">{lead.name}</div>
          <div className="text-white/50 text-sm truncate">{lead.email}{lead.phone ? ` \u2022 ${lead.phone}` : ""}</div>
        </button>
        <div className="text-sm">
          <div className="text-white/75">{lead.projectType || "No project type"}</div>
          <div className="text-white/40 font-mono-x text-xs mt-0.5">{d.toLocaleDateString()} {d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
        </div>
        <Select value={lead.status} onValueChange={(v) => onStatus(lead.id, v)}>
          <SelectTrigger data-testid="lead-status" className={`w-[140px] h-9 rounded-full border-0 text-xs font-bold uppercase tracking-wider ${statusStyle[lead.status]}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/10 text-white">
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1">
          <button onClick={() => setOpen(!open)} className="w-9 h-9 rounded-full text-white/60 hover:bg-white/10 hover:text-white flex items-center justify-center transition-colors">
            <Icons.ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button data-testid="lead-delete" className="w-9 h-9 rounded-full text-white/60 hover:bg-red-500/15 hover:text-red-400 flex items-center justify-center transition-colors"><Icons.Trash2 className="w-4 h-4" /></button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-zinc-950 border-white/10 text-white">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this lead?</AlertDialogTitle>
                <AlertDialogDescription className="text-white/55">The message from {lead.name} will be removed permanently.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-transparent border-white/15 text-white hover:bg-white/10 hover:text-white">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="lead-delete-confirm" onClick={() => onDelete(lead.id)} className="bg-red-500 text-white hover:bg-red-600">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
      {open && (
        <div className="px-5 pb-5 md:pl-[76px]">
          <div className="rounded-xl bg-black/50 border border-white/5 p-4 text-white/75 whitespace-pre-wrap leading-relaxed" data-testid="lead-message">
            {lead.message || <span className="text-white/35 italic">No message provided</span>}
          </div>
          <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-2 mt-3 text-yellow-400 text-sm hover:underline"><Icons.Reply className="w-4 h-4" /> Reply by email</a>
        </div>
      )}
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [data, setData] = useState({ items: [], counts: { total: 0, new: 0, contacted: 0, closed: 0 } });
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (type !== "all") params.type = type;
      if (status !== "all") params.status = status;
      if (q.trim()) params.q = q.trim();
      setData(await fetchLeads(params));
    } catch (err) {
      if (err?.response?.status === 401) onLogout();
      toast.error(errMsg(err, "Could not load leads"));
    } finally { setLoading(false); }
  }, [type, status, q, onLogout]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const onStatus = async (id, s) => {
    try { await updateLeadStatus(id, s); toast.success(`Marked as ${s}`); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const onDelete = async (id) => {
    try { await deleteLead(id); toast.success("Lead deleted"); load(); } catch (err) { toast.error(errMsg(err)); }
  };

  const stats = [
    ["Total Leads", data.counts.total, Icons.Inbox],
    ["New", data.counts.new, Icons.Sparkles],
    ["Contacted", data.counts.contacted, Icons.PhoneCall],
    ["Closed", data.counts.closed, Icons.CheckCircle2],
  ];

  return (
    <div className="min-h-screen bg-black" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-[#0a0a0a]">
        <div className="max-w-[1260px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="w-9 h-9 bg-yellow-400 text-black font-display font-bold text-2xl flex items-center justify-center rounded-[3px]">P</span>
            <span className="font-display font-bold text-xl text-white">Lead Inbox</span>
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={load} data-testid="admin-refresh" className="w-10 h-10 rounded-full border border-white/15 text-white hover:border-yellow-400 hover:text-yellow-400 flex items-center justify-center transition-colors"><Icons.RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></button>
            <button onClick={onLogout} data-testid="admin-logout" className="flex items-center gap-2 border border-white/15 rounded-full px-5 py-2 text-white text-sm hover:bg-white hover:text-black transition-colors"><Icons.LogOut className="w-4 h-4" /> Log out</button>
          </div>
        </div>
      </header>

      <main className="max-w-[1260px] mx-auto px-6 lg:px-10 py-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map(([l, v, I]) => (
            <div key={l} className="rounded-2xl bg-zinc-900 border border-white/10 p-6">
              <I className="w-5 h-5 text-yellow-400" />
              <div className="font-display text-4xl font-bold text-white mt-4" data-testid={`stat-${l.toLowerCase().replace(" ", "-")}`}>{v}</div>
              <div className="text-white/50 text-xs tracking-[0.15em] uppercase mt-1">{l}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-col lg:flex-row gap-4 lg:items-center justify-between">
          <div className="flex gap-2 bg-zinc-900 border border-white/10 rounded-full p-1 w-fit">
            {[["all", "All"], ["quote", "Quotes"], ["contact", "Messages"]].map(([v, l]) => (
              <button key={v} onClick={() => setType(v)} data-testid={`type-${v}`} className={`px-5 py-2 rounded-full text-sm transition-colors ${type === v ? "bg-yellow-400 text-black font-semibold" : "text-white/65 hover:text-white"}`}>{l}</button>
            ))}
          </div>
          <div className="flex gap-3">
            <div className="relative">
              <Icons.Search className="w-4 h-4 text-white/40 absolute left-4 top-1/2 -translate-y-1/2" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, message..." data-testid="admin-search" className="w-full sm:w-72 bg-zinc-900 border border-white/10 rounded-full pl-10 pr-4 py-2.5 text-sm text-white outline-none focus:border-yellow-400 transition-colors" />
            </div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="admin-status-filter" className="w-[150px] rounded-full bg-zinc-900 border-white/10 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10 text-white">
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-6 space-y-3" data-testid="lead-list">
          {!loading && data.items.length === 0 && (
            <div className="rounded-2xl border border-dashed border-white/15 py-20 text-center">
              <Icons.Inbox className="w-10 h-10 text-white/25 mx-auto" />
              <p className="text-white/50 mt-4">No leads yet. New quote requests and messages will appear here.</p>
            </div>
          )}
          {data.items.map((l) => <LeadRow key={l.id} lead={l} onStatus={onStatus} onDelete={onDelete} />)}
        </div>
      </main>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  useEffect(() => {
    const k = getAdminKey();
    if (!k) { setChecking(false); return; }
    verifyAdmin(k).then(() => setAuthed(true)).catch(() => setAdminKey("")).finally(() => setChecking(false));
  }, []);
  const logout = useCallback(() => { setAdminKey(""); setAuthed(false); }, []);
  if (checking) return <div className="min-h-screen bg-black" />;
  return authed ? <Dashboard onLogout={logout} /> : <Login onAuthed={() => setAuthed(true)} />;
}
